import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, useLoad, act } from "@/components/common";

function LinkPicker({ p, reload }) {
  const [list, setList] = useState(null);
  const load = () => api.get("/integrations/basecamp/projects").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  const save = (id) => {
    const bp = list.find((x) => String(x.id) === id);
    act(api.put(`/projects/${p.id}/basecamp-link`, { bc_project_id: bp ? bp.id : null, bc_project_name: bp?.name || "" }), bp ? `Linked to ${bp.name}` : "Unlinked").then(reload);
  };
  if (!list) return <Button size="sm" variant="outline" data-testid="bc-link-btn" onClick={load}>{p.basecamp ? "Change" : "Link Basecamp project"}</Button>;
  return (
    <select className="h-8 rounded border px-2 text-sm" data-testid="bc-link-select" defaultValue={p.basecamp?.project_id || ""} onChange={(e) => save(e.target.value)}>
      <option value="">— not linked —</option>
      {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
    </select>
  );
}

function InboxRow({ u, items, p, done }) {
  const [key, setKey] = useState(u.suggested_item_key || "");
  const open = items.filter((x) => !["delivered", "delivered_late"].includes(x.status) || x.item_key === key);
  const confirm = () => act(api.post(`/projects/${p.id}/basecamp-inbox/${u.id}/confirm`, { item_key: key }), "Delivery logged from Basecamp").then((r) => r && done());
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 py-2 text-sm" data-testid={`bc-inbox-${u.id}`}>
      <div className="min-w-0">
        <a href={u.app_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:underline">{u.filename}<ExternalLink className="h-3 w-3" /></a>
        <div className="text-xs text-slate-500">uploaded {(u.created_at || u.updated_at || "").slice(0, 16).replace("T", " ")} by {u.creator || "unknown"}{u.suggested_item_key ? " · suggested match" : " · no match found"}</div>
      </div>
      <div className="flex items-center gap-2">
        <select className="h-8 max-w-[220px] rounded border px-2 text-xs" value={key} onChange={(e) => setKey(e.target.value)} data-testid={`bc-inbox-item-${u.id}`}>
          <option value="">Choose artefact…</option>
          {open.map((x) => <option key={x.item_key} value={x.item_key}>{x.gate ? `${x.gate} · ` : ""}{x.title}</option>)}
        </select>
        <Button size="sm" className="h-8 text-xs" disabled={!key} data-testid={`bc-inbox-confirm-${u.id}`} onClick={confirm}>Confirm</Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs" data-testid={`bc-inbox-dismiss-${u.id}`} onClick={() => act(api.post(`/projects/${p.id}/basecamp-inbox/${u.id}/dismiss`), "Dismissed").then(done)}>Dismiss</Button>
      </div>
    </div>
  );
}

export function BasecampProject({ p, items, reloadAll }) {
  const { user } = useAuth();
  const st = useLoad(() => api.get("/integrations/basecamp/status").then((r) => r.data));
  const inbox = useLoad(() => api.get(`/projects/${p.id}/basecamp-inbox`).then((r) => r.data), [p.id]);
  if (!st.data?.connected) return null;
  const canLink = !p.locked && (["pmo", "sysadmin"].includes(user.role) || p.pm_id === user.id);
  const done = () => { inbox.reload(); reloadAll(); };
  return (
    <Panel title={`Basecamp uploads waiting for confirmation (${inbox.data?.length || 0})`} testid="bc-inbox-panel"
      action={<div className="flex items-center gap-2 text-xs text-slate-500"><span data-testid="bc-linked-name">{p.basecamp ? `Linked: ${p.basecamp.project_name}` : "Not linked to Basecamp"}</span>{canLink && <LinkPicker p={p} reload={reloadAll} />}</div>}>
      {!p.basecamp ? <p className="text-sm text-slate-500">Link this project to its Basecamp project so new uploads appear here automatically.</p>
        : !(inbox.data || []).length ? <p className="text-sm text-slate-500" data-testid="bc-inbox-empty">No new uploads to confirm. Aspire checks Basecamp every 15 minutes.</p>
          : inbox.data.map((u) => <InboxRow key={u.id} u={u} items={items} p={p} done={done} />)}
    </Panel>
  );
}
